import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FilterTab, Conversation } from '../types';
import ConversationSearch from '../components/ConversationSearch';
import ConversationFilters from '../components/ConversationFilters';
import ConversationListItem from '../components/ConversationListItem';
import EmptyConversationsList from '../components/EmptyConversationsList';
import EmptyChatView from '../components/EmptyChatView';
import ChatView from '../components/ChatView';
import JobDetailsSidebar from '../components/JobDetailsSidebar';
import { conversationStorage, messagingServiceApi } from '../services';
import { applicationApiService } from '../../../candidate/services/applicationApi';
import { jobApiService } from '../../../recruiter/services/jobApi';
import { DavidConversation } from '../types';

interface MessagesProps {
  /**
   * User role - determines UI behavior
   */
  userRole: 'candidate' | 'recruiter';

  /**
   * Current user information
   */
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };

  /**
   * Initial conversation/application ID to auto-select
   */
  initialConversationId?: string;

  /**
   * Callbacks
   */
  onSearchJob?: () => void;

  /**
   * External search query from header search bar
   */
  externalSearchQuery?: string;
}

/**
 * Messages Page
 * Production-grade component for both Candidate and Recruiter dashboards
 * Fetches conversations from API and caches in sessionStorage
 * Shows empty state when user has no conversations
 * Updated: Fixed JSX structure
 */
const Messages: React.FC<MessagesProps> = ({
  userRole,
  user,
  initialConversationId,
  onSearchJob,
  externalSearchQuery
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Sync external search query from header search bar
  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      setSearchQuery(externalSearchQuery);
    }
  }, [externalSearchQuery]);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(initialConversationId || null);

  // Conversation data state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Job detail sidebar state
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobDetails, setSelectedJobDetails] = useState<any>(null);
  const [isLoadingJob, setIsLoadingJob] = useState(false);

  // Draggable panel width
  const MIN_WIDTH = 260;
  const MAX_WIDTH = 560;
  const [panelWidth, setPanelWidth] = useState(420);
  const isDragging = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const container = (e.target as HTMLElement).closest('.messages-container') as HTMLElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, ev.clientX - rect.left));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (filter: FilterTab) => {
    setActiveFilter(filter);
  };

  // Handle job click from David message — fetch full details and show sidebar
  const handleJobClick = useCallback(async (jobId: string) => {
    setSelectedJobId(jobId);
    setIsLoadingJob(true);
    try {
      const job = await jobApiService.getJobById(jobId);
      setSelectedJobDetails({
        job_id: job.id || job._id,
        title: job.title || 'Untitled',
        company_name: job.companyName || 'Unknown Company',
        location: job.location || '',
        job_type: job.employmentType || '',
        description: typeof job.description === 'string' ? job.description : '',
        overview: typeof job.requirements === 'string' ? job.requirements : '',
        salary_range: undefined,
        posted_at: job.createdAt,
      });
    } catch (err) {
      console.error('Failed to fetch job details:', err);
      setSelectedJobDetails({
        job_id: jobId,
        title: 'Job Details',
        company_name: 'Unknown',
      });
    } finally {
      setIsLoadingJob(false);
    }
  }, []);

  const handleCloseJobPanel = useCallback(() => {
    setSelectedJobId(null);
    setSelectedJobDetails(null);
  }, []);

  const handleSearchJob = () => {
    if (onSearchJob) {
      onSearchJob();
    } else {
      // Default: navigate to job search page
      window.location.href = userRole === 'candidate' ? '/candidate/jobs' : '/recruiter/jobs';
    }
  };

  // Fetch conversations on component mount
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch from cache (or API if cache is stale/missing)
        const { conversations: fetchedConversations } = await conversationStorage.getConversations(
          false, // forceRefresh = false (use cache if valid)
          20,    // limit
          0      // skip
        );

        let conversationsToSet = fetchedConversations || [];

        // Handle case when initialConversationId is provided but conversation doesn't exist
        if (initialConversationId) {
          const conversationExists = conversationsToSet.some(
            (c) => c.application_id === initialConversationId
          );

          if (conversationExists) {
            setSelectedConversationId(initialConversationId);
            console.log('Auto-selected existing conversation:', initialConversationId);
          } else {
            // Conversation doesn't exist - create virtual conversation from application
            console.log('Conversation not found, creating virtual conversation for application:', initialConversationId);

            try {
              // Fetch application details
              const application = await applicationApiService.getApplicationById(initialConversationId);
              console.log('Fetched application:', application);

              // Fetch job details to get complete info
              const job = await jobApiService.getJobById(application.jobId);
              console.log('Fetched job:', job);

              // Create virtual conversation object matching Conversation interface
              const virtualConversation: Conversation = {
                id: application.id, // application_id
                application_id: application.id,
                job_id: application.jobId,
                participants: {
                  candidate_id: application.candidateId,
                  recruiter_id: job.recruiterId || '',
                },
                last_message: undefined,
                unread_count: {
                  candidate: 0,
                  recruiter: 0,
                },
                status: 'active' as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                last_message_at: new Date().toISOString(),
                // UI specific fields
                job_title: job.title,
                company_name: job.companyName,
                candidate_name: application.candidateName || 'Candidate',
                is_online: false,
              };

              console.log('Created virtual conversation:', virtualConversation);

              // Add virtual conversation to the list
              conversationsToSet = [virtualConversation, ...conversationsToSet];
              setSelectedConversationId(initialConversationId);
            } catch (fetchError: any) {
              console.error('Failed to create virtual conversation:', fetchError);
              setError('Failed to load conversation details');
            }
          }
        }

        // Fetch David conversations for candidates and merge them in
        if (userRole === 'candidate') {
          try {
            const davidConvos = await messagingServiceApi.getDavidConversations();
            const mappedDavid: Conversation[] = davidConvos.map((dc: DavidConversation) => ({
              id: `david_${dc.id}`,
              application_id: `david_${dc.id}`,
              participants: { candidate_id: dc.candidateId, recruiter_id: 'david' },
              last_message: dc.lastMessage ? {
                content: dc.lastMessage.content,
                sender_id: dc.lastMessage.senderId,
                timestamp: dc.lastMessage.timestamp,
              } : undefined,
              unread_count: { candidate: 0, recruiter: 0 },
              status: 'active' as const,
              created_at: dc.createdAt,
              updated_at: dc.updatedAt,
              last_message_at: dc.lastMessage?.timestamp || dc.updatedAt,
              company_name: 'David',
              job_title: 'Job Recommendations',
              is_online: true,
              is_david: true,
              david_conversation_id: dc.id,
            }));
            conversationsToSet = [...conversationsToSet, ...mappedDavid];
          } catch (davidErr) {
            console.warn('Failed to fetch David conversations:', davidErr);
          }
        }

        // Sort all conversations by most recent message
        conversationsToSet.sort((a, b) =>
          new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
        );

        // Set conversations (with virtual + David conversations if created)
        setConversations(conversationsToSet);
      } catch (err: any) {
        console.error('Failed to fetch conversations:', err);
        setError(err.message || 'Failed to load conversations');
        // Ensure conversations remains an empty array on error
        setConversations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversations();
  }, [initialConversationId]); // Run when initialConversationId changes

  // Filter conversations based on active filter
  const filteredConversations = (conversations || []).filter((conversation) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'david') return conversation.is_david === true;
    if (activeFilter === 'active') return conversation.status === 'active' && !conversation.is_david;
    if (activeFilter === 'declined') return conversation.status !== 'active';
    return true;
  });

  // Further filter by search query — matches name and job title
  const displayedConversations = searchQuery
    ? filteredConversations.filter((conversation) => {
        const q = searchQuery.toLowerCase();
        const displayName = userRole === 'candidate'
          ? conversation.company_name
          : conversation.candidate_name;
        return (
          displayName?.toLowerCase().includes(q) ||
          conversation.job_title?.toLowerCase().includes(q)
        );
      })
    : filteredConversations;

  return (
    <div className="messages-container flex flex-row h-full w-full rounded-2xl shadow-sm border border-[#E5E7EB] bg-white overflow-hidden">
      {/* Left panel: Conversations list */}
      <div
        className="flex flex-col h-full bg-white flex-shrink-0"
        style={{ width: panelWidth }}
      >
        {/* Search box */}
        <div className="flex-shrink-0 px-4 pt-4 pb-2">
          <ConversationSearch
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search conversations..."
          />
        </div>

        {/* Filter tabs */}
        <div className="flex-shrink-0 px-4 pb-3">
          <ConversationFilters
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            userRole={userRole}
          />
        </div>

        {/* Conversation list content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {isLoading ? (
            /* Loading state */
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-3 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
                <p className="font-manrope font-medium text-sm text-[#9CA3AF]">Loading conversations...</p>
              </div>
            </div>
          ) : error ? (
            /* Error state */
            <div className="flex items-center justify-center h-full px-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="1" fill="#DC2626"/>
                  </svg>
                </div>
                <p className="font-manrope font-medium text-sm text-[#DC2626]">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-[#015FA6] text-white rounded-xl font-manrope font-medium text-sm hover:bg-[#014580] active:scale-95 transition-all duration-150"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : displayedConversations.length === 0 ? (
            /* Empty state */
            <EmptyConversationsList onSearchJob={handleSearchJob} />
          ) : (
            /* Conversation list */
            <div className="flex flex-col">
              {displayedConversations.map((conversation) => (
                <ConversationListItem
                  key={conversation.application_id}
                  conversation={conversation}
                  isSelected={selectedConversationId === conversation.application_id}
                  onClick={() => { setSelectedConversationId(conversation.application_id); handleCloseJobPanel(); }}
                  userRole={userRole}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drag handle */}
      <div
        onMouseDown={handleDragStart}
        className="w-1 h-full bg-[#E5E7EB] hover:bg-[#015FA6]/40 cursor-col-resize flex-shrink-0 transition-colors duration-150 active:bg-[#015FA6]/60"
        title="Drag to resize"
      />

      {/* Middle panel: Chat view */}
      <div className="flex-1 min-w-0 h-full overflow-hidden">
        {selectedConversationId && conversations.find((c) => c.application_id === selectedConversationId) ? (
          <ChatView
            conversation={conversations.find((c) => c.application_id === selectedConversationId)!}
            currentUserId={user.id}
            userRole={userRole}
            jobDetails={{
              job_id: conversations.find((c) => c.application_id === selectedConversationId)?.job_id || '',
              title: conversations.find((c) => c.application_id === selectedConversationId)?.job_title || 'Software Engineer',
              company_name: conversations.find((c) => c.application_id === selectedConversationId)?.company_name || 'Company',
              company_logo: conversations.find((c) => c.application_id === selectedConversationId)?.company_logo,
            }}
            onJobClick={handleJobClick}
          />
        ) : selectedConversationId && isLoading ? (
          <div className="flex items-center justify-center h-full bg-gradient-to-b from-[#FAFBFC] to-[#F3F4F6]">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
              <p className="font-manrope font-medium text-sm text-[#9CA3AF]">Loading conversation...</p>
            </div>
          </div>
        ) : (
          <EmptyChatView />
        )}
      </div>

      {/* Right panel: Job details sidebar (3rd pane) */}
      {selectedJobDetails && (
        <>
          <div className="w-px h-full bg-[#E5E7EB] flex-shrink-0" />
          <div className="w-[340px] h-full flex-shrink-0 overflow-hidden">
            {isLoadingJob ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-3 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <JobDetailsSidebar
                jobDetails={selectedJobDetails}
                onClose={handleCloseJobPanel}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Messages;
